import { AxiosError } from "axios";
import { QueryFunctionContext } from "react-query";
import { AssociatedCompany, UserDetailModel } from "../types/UserDetailModel";
import api from "./capTableApi";
import idServerApi from "./idServerApi";
import { queryClient } from "../queries";
import { generateRedirectUrl } from "../shared/utils";
import { getAuthStore } from "../store/useAuthStore";
import { getShareAuthStore } from "../store/useShareAuthStore";
import { useAuthorizationStore } from "../store/useAuthorizationStore";
import { decodeAuthToken } from "../components/utils";

const optionsPrefixUrl = "/v1/auth";
const typedHeaders = { "X-Hissa-Auth-Request": "1" };

export type EquionSession = { type: "EQUION"; userId: string; sid: string };

let currentEquionSession: EquionSession | null = null;
let identityRedirectPending = false;

export const isIdentityRedirectPending = () => identityRedirectPending;

const errorCode = (error: unknown) =>
  (error as AxiosError<{ code?: string }>)?.response?.data?.code ||
  (error as { code?: string })?.code;

const accountChanged = () =>
  Object.assign(new Error("The active Identity account changed"), {
    code: "ACCOUNT_CHANGED",
  });

const sameSession = (a: EquionSession, b: EquionSession) =>
  a.type === b.type && a.userId === b.userId && a.sid === b.sid;

const parseSession = (value: unknown): EquionSession => {
  if (typeof value !== "object" || value === null) throw accountChanged();
  const { type, userId, sid } = value as Record<string, unknown>;
  if (
    type !== "EQUION" ||
    typeof userId !== "string" ||
    !/^\d+$/.test(userId) ||
    typeof sid !== "string" ||
    !sid
  ) {
    throw accountChanged();
  }
  return { type: "EQUION", userId, sid };
};

export const assertTokenUser = (token: string, session: EquionSession) => {
  try {
    const claims = decodeAuthToken(token);
    if (claims.type === "EQUION" && String(claims.id) === session.userId) {
      return;
    }
  } catch {
    // A malformed local token cannot be adopted by a different session.
  }
  throw accountChanged();
};

const retryTransition = async <T>(request: () => Promise<T>): Promise<T> => {
  if (identityRedirectPending) throw accountChanged();
  try {
    return await request();
  } catch (error) {
    if (
      !["RETRY_REFRESH", "SESSION_TRANSITION", "SESSION_CONFLICT"].includes(
        errorCode(error) || ""
      )
    ) {
      throw error;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 250);
    });
    if (identityRedirectPending) throw accountChanged();
    return request();
  }
};

export const getEquionSession = () => currentEquionSession;

export function isEquionSessionInvalid(error: unknown): boolean {
  return (
    (error as AxiosError)?.response?.status === 401 ||
    errorCode(error) === "SESSION_INVALID" ||
    errorCode(error) === "ACCOUNT_CHANGED"
  );
}

export function clearEquionClientState() {
  currentEquionSession = null;
  [
    "accesstoken",
    "accessToken",
    "userId",
    "currentUser",
    "companyId",
    "sharedAccessToken",
  ].forEach((key) => sessionStorage.removeItem(key));
  queryClient.clear();
  getAuthStore().clear();
  getShareAuthStore().clear();
  useAuthorizationStore.getState().clearAuthorization();
}

export function redirectToIdentitySignIn() {
  if (identityRedirectPending) return;
  const redirectUrl = generateRedirectUrl();
  identityRedirectPending = true;
  clearEquionClientState();
  window.location.href = redirectUrl;
}

export const getUserRoleGrants = () =>
  api.get(`${optionsPrefixUrl}/role/grants`).then((res) => res.data.data);

export const getUserCompanies = (): Promise<AssociatedCompany[]> =>
  api.get(`${optionsPrefixUrl}/userCompanies`).then((res) => res.data.data);

export const switchCompanyApi = (companyId: bigint | number | string) =>
  api
    .put(`${optionsPrefixUrl}/company?companyId=${companyId}`)
    .then((res) => res.data);

export const getUserContext = (): Promise<UserDetailModel> =>
  api.get(`${optionsPrefixUrl}/userContext`).then((res) => res.data.data);

export async function checkHeartBeat(): Promise<EquionSession> {
  const response = await retryTransition(() =>
    idServerApi.get("auth/heartbeat", {
      params: { type: "EQUION" },
      headers: typedHeaders,
    })
  );
  if (identityRedirectPending) throw accountChanged();
  const session = parseSession(response.data.data);
  if (currentEquionSession && !sameSession(currentEquionSession, session)) {
    throw accountChanged();
  }
  const token =
    sessionStorage.getItem("accesstoken") || getAuthStore().accessToken;
  if (token) assertTokenUser(token, session);
  currentEquionSession = session;
  return session;
}

export async function refetchAccessToken(
  expectedSession?: EquionSession
): Promise<string> {
  if (
    expectedSession &&
    (!currentEquionSession ||
      !sameSession(expectedSession, currentEquionSession))
  ) {
    throw accountChanged();
  }
  const session = expectedSession || (await checkHeartBeat());
  const response = await retryTransition(() =>
    idServerApi.post(
      "auth/equion/token",
      { expectedSession: session },
      { headers: typedHeaders }
    )
  );
  if (!currentEquionSession || !sameSession(currentEquionSession, session)) {
    throw accountChanged();
  }
  const grant = response.data.data;
  if (
    typeof grant?.accessToken !== "string" ||
    !grant.accessToken ||
    !sameSession(session, parseSession(grant))
  ) {
    throw accountChanged();
  }
  const token = `Bearer ${grant.accessToken}`;
  assertTokenUser(token, session);
  return token;
}

export async function signOut(
  _data?: unknown
): Promise<{ message: string; data: unknown }> {
  let session = currentEquionSession;
  if (!session) {
    try {
      session = await checkHeartBeat();
    } catch (error) {
      if (!isEquionSessionInvalid(error)) throw error;
      return { message: "Logged out successfully", data: null };
    }
  }
  try {
    const response = await idServerApi.post(
      "auth/logout",
      { sid: session.sid },
      { params: { type: "EQUION" }, headers: typedHeaders }
    );
    return response.data;
  } catch (error) {
    if (!isEquionSessionInvalid(error)) throw error;
    return { message: "Logged out successfully", data: null };
  }
}

export async function getPlatformInfo(
  context: QueryFunctionContext
): Promise<any> {
  const accesstoken = context.queryKey[1] as string;
  return idServerApi
    .get(`auth/user/platform`, {
      headers: {
        accesstoken,
      },
    })
    .then((res: any) => res.data.data);
}
